import CryptoJS from 'crypto-js';

import type { welcome } from 'src/app/onboarding/routes';
import { typeToUserSegment } from 'src/app/onboarding/views/welcome/utils';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import {
  readFromLocalStorage,
  useLocalStorage,
} from 'src/hooks/useLocalStorage';
import { absoluteURL } from 'src/lib/absoluteURL';
import type { EventCategory } from 'src/lib/analytics';
import { getGaClientId, setTrackingCookie } from 'src/lib/analytics';
import Config from 'src/lib/config';
import { BackendRouter } from 'src/lib/routers/backend';

import { useTrackCustomEvent } from './useTrackCustomEvent';

type Provider = 'github';

// The key under which the nonce is stored in localStorage
export const NONCE_KEY = 'requestState';

export const useHandleProviderSubmit = ({
  provider,
  callbackUrl,
  setLoggingIn,
  type,
}: {
  provider: Provider;
  callbackUrl: string;
  setLoggingIn?: React.Dispatch<React.SetStateAction<boolean>>;
  type: Parameters<typeof welcome.path>[0]['type'];
}) => {
  const [, setCurrentAccountId] = useCurrentAccountId();
  const [_, setRequestState] = useLocalStorage(NONCE_KEY);
  const trackCustomEvent = useTrackCustomEvent();

  const handleProviderSubmit = async () => {
    setLoggingIn?.(true);

    await setCurrentAccountId(null);

    // eslint-disable-next-line react-hooks/todo -- fix to unblock React Compiler compilation
    try {
      // referrer is sometimes set at runtime, so make sure cookie is up to date.
      const referrer = readFromLocalStorage('utm')?.referrer;
      if (referrer) {
        setTrackingCookie(Config.cookies.Referrer, referrer);
      }

      const trackingGoogleClientId = await getGaClientId();
      if (trackingGoogleClientId) {
        setTrackingCookie(
          Config.cookies.GoogleClientId,
          trackingGoogleClientId,
        );
      }

      setTrackingCookie(Config.cookies.UserSegment, typeToUserSegment(type));

      const nonce = CryptoJS.lib.WordArray.random(32).toString();
      setRequestState(nonce);
      // Append state directly to preserve percent-encoding of nested params in
      // the postLogin value. Using mergeQueryParams + decodeURIComponent here
      // decodes %26 separators inside postLogin, causing orgId/type to be
      // parsed as top-level params by the browser after the backend redirect.
      const callbackWithNonce = `${callbackUrl}&state=${nonce}`;

      BackendRouter.go('LoginWithProviderAndCallback', {
        provider,
        callbackUrl: absoluteURL(callbackWithNonce),
      });
    } finally {
      // do nothing, just wait for redirect
    }
  };
  trackCustomEvent({
    action: 'sign_in_flow',
    // Old event, this style of category is deprecated
    category: 'Sign in with provider' as EventCategory,
    label: 'Studio sign in flow',
    legacyValueDimension: provider,
  });

  return handleProviderSubmit;
};
